/**
 * The single rule for what the UI store writes to storage and what it is
 * allowed to believe when reading it back.
 */

import type { Screen } from '../types';
import { sanitizeSetupRevisionStatus, sanitizeVerifiedSetupBaseline } from '../lib/setupRevision';
import type { SetupRevisionState } from './setupRevisionSlice';
import { isKnownOption, isKnownScreen, normalizeNavigationState } from './uiNavigationState';
import { sanitizeSectionsState } from './uiSectionsState';
import { sanitizeConnectorParameterUndoPlan } from './uiUndoPlanSanitizer';
import type { UIState, UIStore } from './uiStoreState';

/** Everything the store persists; the pending navigation intent deliberately is not here. */
export type PersistedUIState = Pick<
  UIState,
  | 'screen'
  | 'navigationHistory'
  | 'selectedOption'
  | 'sections'
  | 'processConditionsChecked'
  | 'projectPropertiesChecked'
  | 'connectorParameterUndoPlan'
  | 'boltParameterUndoPlan'
  | 'additionalCategoryParameterUndoPlan'
  | 'setupRevisionId'
  | 'setupRevisionStatus'
  | 'setupRevisionProjectKey'
  | 'verifiedSetupBaseline'
  | 'committedWorkflowStep'
>;

export const partializeUIState = (state: UIStore): PersistedUIState => ({
  screen: state.screen,
  navigationHistory: state.navigationHistory,
  selectedOption: state.selectedOption,
  sections: state.sections,
  // defaultPropertiesConfirmed is intentionally not persisted — see merge.
  processConditionsChecked: state.processConditionsChecked,
  projectPropertiesChecked: state.projectPropertiesChecked,
  connectorParameterUndoPlan: state.connectorParameterUndoPlan,
  boltParameterUndoPlan: state.boltParameterUndoPlan,
  additionalCategoryParameterUndoPlan: state.additionalCategoryParameterUndoPlan,
  setupRevisionId: state.setupRevisionId,
  setupRevisionStatus: state.setupRevisionStatus,
  setupRevisionProjectKey: state.setupRevisionProjectKey,
  verifiedSetupBaseline: state.verifiedSetupBaseline,
  committedWorkflowStep: state.committedWorkflowStep,
  // pendingNavigationIntent is never persisted: an unresolved intent belongs to
  // the session that raised it, and restoring one would navigate on reload.
});

const sanitizeRevisionId = (value: unknown): number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : 0;

const sanitizeOptionalText = (value: unknown): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null;

const sanitizeCommittedWorkflowStep = (value: unknown): Screen | null =>
  isKnownScreen(value) ? value : null;

/**
 * A refresh may never upgrade a revision: an in-flight checkpoint comes back as
 * the state it would fall back to, and recovery-required stays blocked.
 */
const sanitizeSetupRevision = (persisted: Partial<UIState>): SetupRevisionState => ({
  setupRevisionId: sanitizeRevisionId(persisted.setupRevisionId),
  setupRevisionStatus: sanitizeSetupRevisionStatus(persisted.setupRevisionStatus),
  setupRevisionProjectKey: sanitizeOptionalText(persisted.setupRevisionProjectKey),
  verifiedSetupBaseline: sanitizeVerifiedSetupBaseline(persisted.verifiedSetupBaseline),
  committedWorkflowStep: sanitizeCommittedWorkflowStep(persisted.committedWorkflowStep),
  pendingNavigationIntent: null,
});

export const validateAndMergeState = (
  persistedState: unknown,
  currentState: UIStore
): UIStore => {
  if (!persistedState || typeof persistedState !== 'object') {
    return currentState;
  }

  const persisted = persistedState as Partial<UIState>;
  const selectedOption = isKnownOption(persisted.selectedOption)
    ? persisted.selectedOption
    : currentState.selectedOption;
  const persistedScreen = isKnownScreen(persisted.screen) ? persisted.screen : currentState.screen;
  const persistedHistory = Array.isArray(persisted.navigationHistory)
    ? persisted.navigationHistory.filter(isKnownScreen)
    : currentState.navigationHistory;
  const normalizedNavigation = normalizeNavigationState(
    persistedScreen,
    persistedHistory,
    selectedOption
  );

  return {
    ...currentState,
    ...normalizedNavigation,
    ...sanitizeSetupRevision(persisted),
    selectedOption,
    sections: sanitizeSectionsState(persisted.sections, selectedOption),
    // defaultPropertiesConfirmed ("Create required parameters") is deliberately
    // session-scoped: enabling it writes parameters into the Revit model, so it
    // must be re-confirmed explicitly on every run, never restored.
    processConditionsChecked:
      typeof persisted.processConditionsChecked === 'boolean'
        ? persisted.processConditionsChecked
        : currentState.processConditionsChecked,
    projectPropertiesChecked:
      typeof persisted.projectPropertiesChecked === 'boolean'
        ? persisted.projectPropertiesChecked
        : currentState.projectPropertiesChecked,
    connectorParameterUndoPlan:
      sanitizeConnectorParameterUndoPlan(persisted.connectorParameterUndoPlan) ??
      currentState.connectorParameterUndoPlan,
    boltParameterUndoPlan:
      sanitizeConnectorParameterUndoPlan(
        persisted.boltParameterUndoPlan,
        { requireProjectCheckpoint: false },
      ) ??
      currentState.boltParameterUndoPlan,
    additionalCategoryParameterUndoPlan:
      sanitizeConnectorParameterUndoPlan(
        persisted.additionalCategoryParameterUndoPlan,
        { requireProjectCheckpoint: false },
      ) ??
      currentState.additionalCategoryParameterUndoPlan,
  };
};
